import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer } from "lucide-react";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n || 0);

export default function BillPrint() {
  const { id } = useParams();
  const [bill, setBill] = useState(null);

  useEffect(() => {
    api.get(`/bills/${id}`).then((r) => setBill(r.data)).catch((e) => console.error("Failed to load bill", e));
  }, [id]);

  if (!bill) return <div className="p-12 text-ink-muted">Loading…</div>;

  const title = bill.doc_type === "challan" ? "Challan" : "Tax Invoice";

  return (
    <div className="min-h-screen bg-white py-8 px-4 print:p-0">
      <div className="max-w-3xl mx-auto flex justify-end mb-4 no-print">
        <button
          onClick={() => window.print()}
          className="btn-primary py-3 flex items-center gap-2"
          data-testid="print-invoice-btn"
        >
          <Printer size={14} strokeWidth={1.75} /> Print
        </button>
      </div>
      <div
        className="max-w-3xl mx-auto bg-white rounded-2xl p-10 shadow-card print:shadow-none print:rounded-none"
        data-testid="printable-invoice"
      >
        <div className="flex items-start justify-between border-b border-neutral-200 pb-6 mb-8">
          <div>
            <div className="font-display text-4xl font-semibold tracking-tight text-forest-700">
              Payments
            </div>
            <div className="text-xs uppercase tracking-widest text-ink-muted mt-1">
              {title}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-ink-muted">
              {bill.doc_type === "challan" ? "Challan" : "Bill"} #
            </div>
            <div className="font-mono-num text-xl font-semibold">{bill.bill_number}</div>
            <div className="mt-2 text-xs uppercase tracking-widest text-ink-muted">
              Date
            </div>
            <div className="font-mono-num text-sm">{bill.bill_date}</div>
            {bill.gstin && (
              <>
                <div className="mt-2 text-xs uppercase tracking-widest text-ink-muted">
                  GST
                </div>
                <div className="font-mono-num text-sm">GST {bill.gstin}</div>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-ink-muted mb-2">
              Buyer
            </div>
            <div className="font-display font-semibold text-xl">
              {bill.buyer?.name || "—"}
            </div>
            {bill.buyer?.address && (
              <div className="text-sm whitespace-pre-line text-ink-soft mt-1">
                {bill.buyer.address}
              </div>
            )}
            {bill.buyer?.contact && (
              <div className="text-sm text-ink-soft">{bill.buyer.contact}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest text-ink-muted mb-2">
              Handled by
            </div>
            <div className="font-display font-semibold text-lg">
              {bill.employee?.name || "—"}
            </div>
            <div className="mt-4 text-[11px] uppercase tracking-widest text-ink-muted">
              Age
            </div>
            <div className="font-mono-num text-sm">
              {bill.days_since ?? 0} days since {bill.doc_type === "challan" ? "challan" : "bill"}
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-bone-50 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-widest text-ink-muted">
              Amount
            </div>
            <div className="font-mono-num text-3xl font-semibold text-forest-700">
              {inr(bill.amount)}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-ink-muted">Paid</span>
              <span className="font-mono-num">{inr(bill.paid_amount)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-ink-muted">Balance</span>
              <span
                className={`font-mono-num font-semibold ${
                  bill.balance > 0.001 ? "text-terracotta-700" : "text-forest-700"
                }`}
              >
                {inr(bill.balance)}
              </span>
            </div>
          </div>
        </div>

        {bill.payments && bill.payments.length > 0 && (
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-widest text-ink-muted mb-3">
              Payment history
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-ink-muted">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Note</th>
                  <th className="text-right py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {bill.payments.map((p, i) => (
                  <tr key={`${p.date}-${p.amount}-${p.mode}-${i}`} className="border-b border-neutral-100">
                    <td className="py-2 font-mono-num text-ink-soft">
                      {(p.date || "").slice(0, 10)}
                    </td>
                    <td className="py-2 text-ink-soft">{p.note || "—"}</td>
                    <td className="py-2 text-right font-mono-num">{inr(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {bill.notes && (
          <div className="border-t border-neutral-200 pt-6">
            <div className="text-[11px] uppercase tracking-widest text-ink-muted mb-1">
              Notes
            </div>
            <div className="text-sm text-ink-soft whitespace-pre-line">{bill.notes}</div>
          </div>
        )}

        <div className="mt-16 flex items-end justify-between text-xs text-ink-muted">
          <div>Thank you for your business.</div>
          <div className="text-right">
            <div className="border-t border-ink pt-2 min-w-[180px]">
              Authorised signatory
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
